import React, { useEffect, useState } from "react";
import moment from "moment";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import DateSelector from "../../../ui/DateSelector";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { editCouponValidationSchema, COUPON_TYPE_OPTIONS } from "../schema";
import { editCoupon } from "../../../../redux/features/CouponSlice";
import type { UpdateCouponPayload } from "../../../../apis/coupons";

interface EditCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  /** Raw coupon from list_coupons (camelCase dates in response). */
  selectedCoupon: any;
}

type EditFormState = {
  expiry_date: string;
  couponType: string;
  couponValueType: "" | "AMOUNT" | "PERCENTAGE";
  couponAmount: string;
  couponDiscount: string;
};

const emptyFormState: EditFormState = {
  expiry_date: "",
  couponType: "",
  couponValueType: "",
  couponAmount: "",
  couponDiscount: "",
};

const emptyErrors: Record<keyof EditFormState, string> = {
  expiry_date: "",
  couponType: "",
  couponValueType: "",
  couponAmount: "",
  couponDiscount: "",
};

const prettifyCouponType = (raw: string): string =>
  raw
    ?.split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ") ?? "";

/** Format a Date as YYYY-MM-DD using local components (no UTC drift). */
const formatLocalYmd = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/** YYYY-MM-DD -> ISO datetime at end-of-day UTC (matches Create modal). */
const toEndOfDayIso = (dateStr: string): string => {
  if (!dateStr) return "";
  return `${dateStr}T23:59:59.999Z`;
};

const EditCouponModal: React.FC<EditCouponModalProps> = ({
  open,
  onClose,
  onSave,
  selectedCoupon,
}) => {
  const dispatch = useAppDispatch();
  const { updating } = useAppSelector((state: RootState) => state.coupons);

  const [formData, setFormData] = useState<EditFormState>(emptyFormState);
  const [errors, setErrors] =
    useState<Record<keyof EditFormState, string>>(emptyErrors);

  // Hydrate the form from the selected coupon every time the modal opens.
  useEffect(() => {
    if (!open || !selectedCoupon) return;

    const hasAmount =
      selectedCoupon.couponAmount != null && selectedCoupon.couponAmount !== "";

    setFormData({
      expiry_date: selectedCoupon.expiryDate
        ? moment(selectedCoupon.expiryDate).format("YYYY-MM-DD")
        : "",
      couponType: selectedCoupon.couponType ?? "",
      couponValueType: hasAmount ? "AMOUNT" : "PERCENTAGE",
      couponAmount:
        selectedCoupon.couponAmount != null
          ? String(selectedCoupon.couponAmount)
          : "",
      couponDiscount:
        selectedCoupon.couponDiscount != null
          ? String(selectedCoupon.couponDiscount)
          : "",
    });
    setErrors(emptyErrors);
  }, [open, selectedCoupon]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (name: keyof EditFormState, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = async () => {
    try {
      await editCouponValidationSchema.validate(formData, {
        abortEarly: false,
      });
      return true;
    } catch (err: any) {
      const validationErrors: Record<string, string> = {};
      err.inner.forEach((error: any) => {
        if (!validationErrors[error.path]) {
          validationErrors[error.path] = error.message;
        }
      });
      setErrors((prev) => ({ ...prev, ...validationErrors }));
      return false;
    }
  };

  const buildPayload = (): UpdateCouponPayload => {
    const payload: UpdateCouponPayload = {
      expiry_date: toEndOfDayIso(formData.expiry_date),
      couponType: formData.couponType,
    };

    if (formData.couponValueType === "AMOUNT") {
      payload.couponAmount = Number(formData.couponAmount);
    } else if (formData.couponValueType === "PERCENTAGE") {
      payload.couponDiscount = Number(formData.couponDiscount);
    }

    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    if (!selectedCoupon?.id) {
      toast.error("Missing coupon id.");
      return;
    }

    const result = await dispatch(
      editCoupon({
        couponId: selectedCoupon.id,
        payload: buildPayload(),
      })
    );

    if (editCoupon.fulfilled.match(result)) {
      toast.success(result.payload?.message || "Coupon updated successfully");
      onClose();
      onSave();
    } else {
      toast.error((result.payload as string) || "Failed to update coupon");
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="toolbar"
            variant="ghost"
          />
          <Button
            type="submit"
            label="Save changes"
            onClick={handleSubmit}
            isLoading={updating}
            layout="toolbar"
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-700">
            Coupon code
          </label>
          <input
            type="text"
            readOnly
            value={selectedCoupon?.couponCode ?? ""}
            className="w-full border rounded px-3 py-2 text-sm bg-gray-50 text-gray-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            Coupon code cannot be changed.
          </p>
        </div>

        <DateSelector
          label="Expiry date"
          name="expiry_date"
          value={formData.expiry_date}
          onChange={(date) => {
            setFormData((prev) => ({
              ...prev,
              expiry_date: date ? formatLocalYmd(date) : "",
            }));
            setErrors((prev) => ({ ...prev, expiry_date: "" }));
          }}
          placeholder="Select expiry date"
          error={errors.expiry_date}
          disablePast
          required
        />

        <Dropdown
          label="Coupon type"
          name="couponType"
          options={COUPON_TYPE_OPTIONS.map((t) => ({
            label: prettifyCouponType(t),
            value: t,
          }))}
          placeholder="Select coupon type"
          value={formData.couponType}
          onSelect={(value) => handleSelectChange("couponType", value)}
          error={errors.couponType}
          required
        />

        <Dropdown
          label="Value type"
          name="couponValueType"
          options={[
            { label: "Amount (₦)", value: "AMOUNT" },
            { label: "Percentage (%)", value: "PERCENTAGE" },
          ]}
          placeholder="Select value type"
          value={formData.couponValueType}
          onSelect={(value) =>
            handleSelectChange("couponValueType", value as string)
          }
          error={errors.couponValueType}
          required
        />

        {formData.couponValueType === "AMOUNT" && (
          <ModalInput
            label="Coupon amount (₦)"
            name="couponAmount"
            type="number"
            value={formData.couponAmount}
            onChange={handleChange}
            placeholder="Enter coupon amount"
            error={errors.couponAmount}
            required
          />
        )}

        {formData.couponValueType === "PERCENTAGE" && (
          <ModalInput
            label="Coupon discount (%)"
            name="couponDiscount"
            type="number"
            value={formData.couponDiscount}
            onChange={handleChange}
            placeholder="Enter discount"
            error={errors.couponDiscount}
            required
          />
        )}
      </form>
    </CustomModalContainer>
  );
};

export default EditCouponModal;
